import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Trash2, Sparkles, X, UserRoundCog, Camera, Pencil, Check, Flame, ChefHat, HeartPulse, Boxes, Dumbbell, Pill as PillIcon, Image as ImageIcon, Square, RotateCcw, User, History, Plus } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import FloatingWaterDrop from "@/components/FloatingWaterDrop";
import DeductionDialog from "@/components/DeductionDialog";
import MealReviewDialog from "@/components/MealReviewDialog";
import RecipeCardDialog from "@/components/RecipeCardDialog";
import WeekInPatterns from "@/components/WeekInPatterns";
import ChallengeBadge from "@/components/ChallengeBadge";
import SavedPlanCard from "@/components/SavedPlanCard";
import Markdown from "@/components/Markdown";
import VoiceButton from "@/components/VoiceButton";

const DIET_LABEL = {
  veg: "vegetarian", vegetarian: "vegetarian", vegan: "vegan",
  nonveg: "non-vegetarian", non_veg: "non-vegetarian", "non-vegetarian": "non-vegetarian",
  eggetarian: "eggetarian", pescatarian: "pescatarian", keto: "keto", jain: "Jain",
};
const GOAL_LABEL = { lose: "to lose weight", gain: "to build muscle", maintain: "to maintain my weight" };

// A neutral time-of-day line used as the overline when a personalized memory greeting
// is present — avoids stacking two greetings (and the name twice) in the empty state.
function timeGreeting() {
  const h = new Date().getHours();
  return h < 12 ? "good morning" : h < 17 ? "good afternoon" : "good evening";
}

const DORMANT_MS = 10 * 60 * 1000; // 10 min of no use → fresh blank chat on return
const markActive = () => { try { localStorage.setItem("kaaya_last_active", String(Date.now())); } catch (_) {} };

function buildPlanText(profile) {
  if (!profile) return "Plan a healthy day for me ";
  const dp = (profile.dietary_preference || "").toLowerCase();
  const diet = dp && dp !== "none" && DIET_LABEL[dp] ? `${DIET_LABEL[dp]} ` : "";
  const cals = profile?.macro_targets?.calories ? ` around ${Math.round(profile.macro_targets.calories)} kcal` : "";
  const goal = profile.goal && GOAL_LABEL[profile.goal] ? ` ${GOAL_LABEL[profile.goal]}` : "";
  return `Plan a healthy ${diet}day for me${cals}${goal} `.replace(/\s{2,}/g, " ");
}

function buildStarters(profile) {
  return [
    { label: "What should I eat?", text: "What should I eat today? " },
    { label: "How am I doing?", text: "How am I doing this week? " },
    { label: "Cook with what I have", text: "What can I cook with - " },
  ];
}

// Module-level caches survive route unmount/remount so returning to chat is instant.
const chatSessionsCache = { list: null };
const chatMsgCache = {}; // { [sessionId]: messages[] }

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [challengeRefresh, setChallengeRefresh] = useState(0);
  const [sessions, setSessions] = useState(chatSessionsCache.list || []);
  const [weekly, setWeekly] = useState(null);
  useEffect(() => { api.get("/insights/weekly").then((r) => setWeekly(r.data)).catch(() => {}); }, []);
  const [freshStart, setFreshStart] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [partial, setPartial] = useState("");
  const [activePlan, setActivePlan] = useState(null);
  const [planReady, setPlanReady] = useState(false);
  const [stalled, setStalled] = useState(false);
  const [deductTrigger, setDeductTrigger] = useState(null);
  const [reviewTrigger, setReviewTrigger] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem("onboarding_banner_dismissed") === "1"
  );
  const scroller = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const lastActivityRef = useRef(0);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [photoDraft, setPhotoDraft] = useState(null); // { file, url }
  const [photoNote, setPhotoNote] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [recipeOpen, setRecipeOpen] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [recipeFavs, setRecipeFavs] = useState([]);
  const [cooking, setCooking] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportType, setSupportType] = useState("support");
  const [supportText, setSupportText] = useState("");
  const [supportSubmitting, setSupportSubmitting] = useState(false);

  const favKey = (dish, meal) => `${(dish || "").toLowerCase().trim()}|${meal || "snack"}`;
  const loadRecipeFavs = async () => {
    try { const { data } = await api.get("/recipes/favorites"); setRecipeFavs(data || []); } catch (e) { console.error(e); }
  };

  const openRecipe = async (sug) => {
    if (!sug?.dish) return;
    setRecipeOpen(true);
    setRecipe(null);
    setRecipeLoading(true);
    loadRecipeFavs();
    try {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await api.post("/planner/recipe", { date: today, meal: sug.meal || "snack", dish_name: sug.dish });
      setRecipe(data);
    } catch (e) {
      toast.error("Couldn't build the recipe card — please retry");
      setRecipeOpen(false);
    } finally {
      setRecipeLoading(false);
    }
  };

  const savePlan = async (suggestions) => {
    try {
      await api.post("/plans/save", { meals: (suggestions || []).map((s) => ({ dish: s.dish, meal: s.meal })) });
      toast.success("Plan saved — tick meals off in your Tracker.");
    } catch { toast.error("Couldn't save the plan."); }
  };

  const toggleLikeRecipe = async () => {
    if (!recipe) return;
    const existing = recipeFavs.find((f) => favKey(f.dish, f.meal) === favKey(recipe.dish, recipe.meal));
    try {
      if (existing) {
        await api.delete(`/recipes/favorites/${existing.id}`);
        toast("Removed from your liked recipes");
      } else {
        await api.post("/recipes/favorites", { dish: recipe.dish, meal: recipe.meal || "snack", date: recipe.date || "", recipe });
        toast.success("Saved ❤ — added to your Planner's liked recipes");
      }
      await loadRecipeFavs();
    } catch (e) {
      toast.error("Couldn't update liked recipes");
    }
  };

  const cookRecipe = async (r) => {
    if (!r) return;
    setCooking(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const t = r.totals || {};
      const { data: log } = await api.post("/food-logs", {
        name: r.dish,
        serving: r.servings ? `${r.servings} serving${r.servings > 1 ? "s" : ""}` : "1 serving",
        meal: ["breakfast", "lunch", "dinner", "snack"].includes(r.meal) ? r.meal : "snack",
        calories: Number(t.calories) || 0, protein_g: Number(t.protein_g) || 0,
        carbs_g: Number(t.carbs_g) || 0, fat_g: Number(t.fat_g) || 0, fiber_g: Number(t.fiber_g) || 0,
        logged_for: today,
      });
      toast.success(`Logged "${r.dish}" to your tracker`);
      setRecipeOpen(false);
      setReviewTrigger({
        log_id: log.id, name: r.dish,
        ingredients: (r.ingredients || []).map((ing) => ({
          name: ing.name, quantity: ing.quantity, unit: ing.unit,
          calories: ing.calories, protein_g: ing.protein_g, carbs_g: ing.carbs_g, fat_g: ing.fat_g, fiber_g: ing.fiber_g,
        })),
      });
    } catch (e) {
      toast.error("Couldn't log this meal");
    } finally {
      setCooking(false);
    }
  };

  const recipeLiked = recipe ? recipeFavs.some((f) => favKey(f.dish, f.meal) === favKey(recipe.dish, recipe.meal)) : false;

  const pickPhoto = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large (max 10MB)"); return; }
    setPhotoNote("");
    setPhotoDraft({ file, url: URL.createObjectURL(file) });
  };

  const closePhotoDraft = () => {
    if (photoDraft?.url) URL.revokeObjectURL(photoDraft.url);
    setPhotoDraft(null);
    setPhotoNote("");
  };

  const fillStarter = (text) => {
    setInput(text);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) { el.focus(); el.setSelectionRange(text.length, text.length); }
    });
  };

  const onStarter = (s) => {
    if (s.kind === "support") { setSupportType("support"); setSupportText(""); setSupportOpen(true); return; }
    fillStarter(s.text);
  };

  const submitSupport = async () => {
    const msg = supportText.trim();
    if (!msg) { toast.error("Please write a message first."); return; }
    setSupportSubmitting(true);
    try {
      await api.post("/support", { type: supportType, message: msg });
      toast.success(supportType === "feedback" ? "Thanks for the feedback — our team will review it." : "Support request sent — we'll get back to you soon.");
      setSupportOpen(false);
      setSupportText("");
    } catch (e) {
      toast.error("Could not send — please try again.");
    } finally {
      setSupportSubmitting(false);
    }
  };

  const loadSessions = async (resume = true) => {
    try {
      const { data } = await api.get("/chat/sessions");
      chatSessionsCache.list = data || [];
      setSessions(data || []);
      // Continuous chat: resume the most recent conversation as the single ongoing thread.
      if (resume && data && data.length) setCurrentId((cur) => cur || data[0].id);
    } catch (e) { console.error("Failed to load chat sessions:", e); }
  };

  // Decide on mount whether to resume the last chat or start blank.
  // Blank (fresh) when: the app was just launched/killed-and-reopened (no in-session flag),
  // OR there's been >10 min of no use. Otherwise (in-app tab nav) we resume.
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return; // guard React StrictMode double-invoke
    didInit.current = true;
    const freshDoc = !sessionStorage.getItem("kaaya_chat_started");
    const leftChat = !!sessionStorage.getItem("kaaya_chat_left");
    let last = 0;
    try { last = parseInt(localStorage.getItem("kaaya_last_active") || "0", 10); } catch (_) {}
    const dormant = last && (Date.now() - last > DORMANT_MS);
    if (freshDoc || dormant || leftChat) {
      setFreshStart(true);
      loadSessions(false); // know whether history exists, but stay blank
    } else {
      // Resume: show the cached thread instantly, then revalidate in the background.
      if (chatSessionsCache.list && chatSessionsCache.list.length) {
        setCurrentId((cur) => cur || chatSessionsCache.list[0].id);
      }
      loadSessions(true);  // resume the ongoing thread
    }
    sessionStorage.setItem("kaaya_chat_started", "1");
    sessionStorage.removeItem("kaaya_chat_left");
    markActive();
  }, []);

  // Leaving the chat screen (navigating to Tracker / Planner / etc.) marks it so the
  // next return to chat opens a fresh blank screen with a "Pull previous chat" option.
  useEffect(() => {
    return () => { try { sessionStorage.setItem("kaaya_chat_left", "1"); } catch (_) {} };
  }, []);

  // If the app is backgrounded and returns after >10 min idle, reset to a blank chat.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        markActive();
        return;
      }
      let last = 0;
      try { last = parseInt(localStorage.getItem("kaaya_last_active") || "0", 10); } catch (_) {}
      if (last && Date.now() - last > DORMANT_MS && !streaming) {
        setMessages([]);
        setCurrentId(null);
        setPartial("");
        setActivePlan(null);
        setPlanReady(false);
        setFreshStart(true);
        loadSessions(false);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [streaming]);

  const loadHistory = () => {
    setFreshStart(false);
    markActive();
    // Show the cached thread instantly, then revalidate.
    if (chatSessionsCache.list && chatSessionsCache.list.length) {
      setCurrentId((cur) => cur || chatSessionsCache.list[0].id);
    }
    loadSessions(true);
  };

  useEffect(() => {
    if (currentId) {
      // Show cached messages instantly (if we have them) while we revalidate.
      if (chatMsgCache[currentId]) setMessages(chatMsgCache[currentId]);
      api.get(`/chat/sessions/${currentId}/messages`).then(({ data }) => {
        // Defensively drop any blank assistant bubbles (e.g. saved during an earlier LLM error).
        const msgs = (data.messages || []).filter(
          (m) => m.role !== "assistant" || (m.content && m.content.trim()) || m.logged || m.recipe_suggestion || (m.recipe_suggestions && m.recipe_suggestions.length)
        );
        setMessages(msgs);
        chatMsgCache[currentId] = msgs;
      });
    } else {
      setMessages([]);
    }
  }, [currentId]);

  // Keep the in-memory message cache hot so returning to chat is instant.
  useEffect(() => {
    if (currentId && !streaming && messages.length) chatMsgCache[currentId] = messages;
  }, [messages, currentId, streaming]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
  }, [messages, partial]);

  // One streaming round-trip. Returns the parsed result; side-effects (toasts, refresh,
  // logged events) are applied inline. Throws on abort / network error.
  const getWeatherContext = () => {
    try {
      const cached = JSON.parse(localStorage.getItem("kaaya_weather") || "null");
      if (cached?.data) {
        const { c, label } = cached.data;
        return `${c}°C${label ? ` (${label})` : ""}`;
      }
    } catch (_) {}
    return null;
  };

  // Undo a just-logged entry (food / workout / water) from the chat confirmation toast.
  const undoLog = async (kind, id) => {
    if (!id) return;
    try {
      await api.delete(`/${kind}/${id}`);
      toast.success("Removed");
      loadSessions();
      refresh();
    } catch (e) {
      toast.error("Couldn't undo — try removing it from the Tracker.");
    }
  };


  const streamOnce = async (sessionId, msg, controller) => {
    const token = localStorage.getItem("kaaya_token");
    const res = await fetch(`${API}/chat/message`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
      signal: controller.signal,
      body: JSON.stringify({ session_id: sessionId, message: msg, weather: getWeatherContext() || undefined }),
    });
    if (!res.ok) {
      const err = new Error(`HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let acc = "";
    let newSessionId = sessionId;
    let logged = null;
    let plan = [];
    let recipeSug = null;
    let recipesSug = null;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      // Any bytes (including server keepalives every ~8s) count as a live connection.
      lastActivityRef.current = Date.now();
      setStalled(false);
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n\n");
      buffer = events.pop() || "";
      for (const ev of events) {
        const line = ev.split("\n").find((l) => l.startsWith("data:"));
        if (!line) continue;
        try {
          const payload = JSON.parse(line.replace(/^data:\s?/, ""));
          if (payload.plan) {
            plan = payload.plan;
            setActivePlan(payload.plan);
          }
          if (payload.plan_done) setPlanReady(true);
          if (payload.delta) {
            acc += payload.delta;
            setPartial(acc);
          }
          if (payload.logged) logged = payload.logged;
          if (payload.recipe_suggestion) recipeSug = payload.recipe_suggestion;
          if (payload.recipe_suggestions) recipesSug = payload.recipe_suggestions;
          if (payload.logged_workout) {
            const w = payload.logged_workout;
            toast.success(`Workout logged: ${w.name} (${Math.round(w.calories_burned)} kcal burned)`, {
              action: { label: "Undo", onClick: () => undoLog("workouts", w.id) },
            });
          }
          if (payload.logged_water) {
            const wa = payload.logged_water;
            toast.success(`Water logged: ${Math.round(wa.amount_ml)} ml`, {
              action: { label: "Undo", onClick: () => undoLog("water", wa.id) },
            });
          }
          if (payload.profile_updated) {
            const keys = Object.keys(payload.profile_updated).join(", ");
            toast.success(`Profile updated (${keys})`);
            refresh();
          }
          if (payload.macros_updated) {
            const m = payload.macros_updated;
            toast.success(`Daily targets set — ${Math.round(m.calories)} kcal • P${Math.round(m.protein_g)} C${Math.round(m.carbs_g)} F${Math.round(m.fat_g)}`);
            refresh();
          }
          if (payload.macros_locked) {
            toast.warning("Macros are admin-locked — ask your coach to adjust.");
          }
          if (payload.error) toast.error(payload.error);
          if (payload.challenge_updated) setChallengeRefresh((x) => x + 1);
          if (payload.done) newSessionId = payload.session_id || newSessionId;
        } catch (err) { console.error("Failed to parse chat SSE payload:", err); }
      }
    }

    const displayed = acc.replace(/\n?LOG_FOOD:\s*\{[\s\S]*?\}\s*$/m, "").trim();
    return { displayed, logged, plan, recipeSug, recipesSug, newSessionId };
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setFreshStart(false);
    markActive();
    setStreaming(true);
    setPartial("");
    setActivePlan(null);
    setPlanReady(false);

    // optimistic user msg
    const tempUser = { id: `tmp-${Date.now()}`, role: "user", content: msg, created_at: new Date().toISOString() };
    setMessages((m) => [...m, tempUser]);

    const controller = new AbortController();
    abortRef.current = controller;

    // Watchdog: if no bytes arrive for ~20s (keepalives stop), surface a "reconnecting" hint.
    lastActivityRef.current = Date.now();
    setStalled(false);
    const watchdog = setInterval(() => {
      if (Date.now() - lastActivityRef.current > 20000) setStalled(true);
    }, 4000);

    try {
      let result = await streamOnce(currentId, msg, controller);
      const isEmpty = (r) => !r.displayed && !r.logged && !r.recipeSug && !(r.recipesSug && r.recipesSug.length);
      // Auto-retry once if the stream came back empty (e.g. the connection dropped or
      // the tab was throttled/backgrounded before any token arrived) — common on mobile.
      if (isEmpty(result) && !controller.signal.aborted) {
        setPartial("");
        setActivePlan(null);
        setPlanReady(false);
        lastActivityRef.current = Date.now();
        setStalled(false);
        result = await streamOnce(result.newSessionId, msg, controller);
      }

      const { displayed, logged, plan, recipeSug, recipesSug, newSessionId } = result;
      if (isEmpty(result)) {
        // Definitively no reply — keep the user bubble and flag it so a "Tap to retry"
        // affordance renders on it (no duplicate bubbles, one-tap recovery).
        setMessages((m) => m.map((x) => (x.id === tempUser.id ? { ...x, failed: true } : x)));
        toast.error("The reply didn't come through — tap retry.");
      } else {
        setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: displayed, plan, logged, recipe_suggestion: recipeSug, recipe_suggestions: recipesSug, created_at: new Date().toISOString() }]);
      }
      setPartial("");
      setActivePlan(null);
      setPlanReady(false);

      if (!currentId) setCurrentId(newSessionId);
      if (logged) {
        toast.success(`Logged: ${logged.name} (${Math.round(logged.calories)} kcal)`, {
          action: { label: "Undo", onClick: () => undoLog("food-logs", logged.id) },
        });
        setReviewTrigger({
          log_id: logged.id, name: logged.name, serving: logged.serving, notes: logged.notes,
          calories: logged.calories, protein_g: logged.protein_g, carbs_g: logged.carbs_g,
          fat_g: logged.fat_g, fiber_g: logged.fiber_g,
        });
      }
      loadSessions();
    } catch (e) {
      if (e.name === "AbortError") {
        // User stopped generation — keep whatever streamed so far as the reply.
        setPartial((p) => {
          const stopped = (p || "").replace(/\n?[A-Z_]+:\s*\{[\s\S]*$/m, "").trim();
          if (stopped) {
            setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: stopped, stopped: true, created_at: new Date().toISOString() }]);
          } else {
            setMessages((m) => m.filter((x) => x.id !== tempUser.id));
          }
          return "";
        });
        loadSessions();
      } else {
        const status = e.status;
        if (status === 401) {
          toast.error("Your session expired — please sign in again.");
          setTimeout(() => { window.location.href = "/"; }, 1500);
        } else if (status === 402) {
          toast.error("Your access has ended — redirecting to billing.");
          setTimeout(() => { window.location.href = "/billing"; }, 1200);
        } else if (status === 404 || status === 502 || status === 503) {
          toast.error("Couldn't reach KAAYA — your app may be out of date. Please refresh the page (⌘/Ctrl+Shift+R) and try again.");
          setMessages((m) => m.map((x) => (x.id === tempUser.id ? { ...x, failed: true } : x)));
        } else {
          toast.error("Chat failed: " + (e.message || "error"));
          setMessages((m) => m.map((x) => (x.id === tempUser.id ? { ...x, failed: true } : x)));
        }
      }
    } finally {
      clearInterval(watchdog);
      setStalled(false);
      abortRef.current = null;
      setStreaming(false);
      setActivePlan(null);
      setPlanReady(false);
    }
  };

  const stopStreaming = () => {
    abortRef.current?.abort();
  };

  const retry = (failedMsg) => {
    if (streaming || !failedMsg?.content) return;
    setMessages((m) => m.filter((x) => x.id !== failedMsg.id));
    send(failedMsg.content);
  };

  const editLog = (logged) => {
    if (!logged) return;
    setReviewTrigger({
      log_id: logged.id, name: logged.name, serving: logged.serving, notes: logged.notes,
      calories: logged.calories, protein_g: logged.protein_g, carbs_g: logged.carbs_g,
      fat_g: logged.fat_g, fiber_g: logged.fiber_g,
    });
  };

  const uploadMealPhoto = async (file, note = "") => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB)");
      return;
    }
    setStreaming(true);
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: note?.trim() ? `📸 ${note.trim()}` : "📸 [analyzing photo…]" }]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (note?.trim()) fd.append("caption", note.trim());
      if (currentId) fd.append("session_id", currentId);
      const token = localStorage.getItem("kaaya_token");
      const res = await fetch(`${API}/chat/meal-photo`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (data.session_id) {
        if (!currentId) setCurrentId(data.session_id);
        const { data: sess } = await api.get(`/chat/sessions/${data.session_id}/messages`);
        setMessages(sess.messages || []);
      }
      if (data.log) {
        toast.success(`Logged: ${data.log.name} (${Math.round(data.log.calories)} kcal)`, {
          action: { label: "Undo", onClick: () => undoLog("food-logs", data.log.id) },
        });
        setReviewTrigger({
          log_id: data.log.id, name: data.log.name, serving: data.log.serving, notes: data.log.notes,
          calories: data.log.calories, protein_g: data.log.protein_g, carbs_g: data.log.carbs_g,
          fat_g: data.log.fat_g, fiber_g: data.log.fiber_g,
        });
        loadSessions();
      } else if (data.kind === "body") {
        toast.success("Body analysis ready");
        loadSessions();
      } else {
        loadSessions();
      }
    } catch (e) {
      toast.error("Photo analysis failed");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setStreaming(false);
    }
  };

  return (
    <Layout>
      <FloatingWaterDrop />
      <div className="flex flex-col h-full md:h-[calc(100vh-77px)] md:grid md:grid-cols-12 md:gap-6 md:max-w-7xl md:mx-auto md:px-6 md:py-6">
        {/* Chat */}
        <section className="flex-1 min-h-0 md:col-span-12 flex flex-col bg-surface-elevated md:rounded-3xl md:border md:border-border overflow-hidden">
          {!user?.profile?.onboarding_complete && !bannerDismissed && (
            <div className="px-5 py-3 bg-secondary/10 border-b border-secondary/30 flex items-center gap-3" data-testid="onboarding-banner">
              <UserRoundCog size={18} className="text-secondary shrink-0" />
              <div className="text-sm flex-1">
                <span className="font-medium text-primary">Tell me about you</span>
                <span className="text-foreground/70"> — share your age, weight, goal, or diet in chat. I'll personalize macros and recipes silently. </span>
                <Link to="/profile" className="underline text-primary font-medium" data-testid="onboarding-banner-link">Or fill it in directly →</Link>
              </div>
              <button
                onClick={() => { setBannerDismissed(true); localStorage.setItem("onboarding_banner_dismissed", "1"); }}
                className="text-muted-foreground hover:text-foreground"
                data-testid="onboarding-banner-close"
              ><X size={16} /></button>
            </div>
          )}
          <div className="px-6 md:px-10 pt-4 pb-1 shrink-0">
            <ChallengeBadge refreshSignal={sessions.length + challengeRefresh} />
          </div>
          <div ref={scroller} className="flex-1 overflow-y-auto scrollbar-thin px-6 md:px-10 py-8" data-testid="chat-scroller">
            {messages.length === 0 && !partial && (
              <div className="max-w-2xl mx-auto pt-2 stagger-fade-in">
                <div className="text-overline mb-2" data-testid="chat-empty-overline">
                  {weekly?.memory_greeting
                    ? timeGreeting()
                    : (weekly?.has_data && weekly?.days_since_last_log >= 3)
                      ? `We missed you${user?.name ? ", " + user.name.split(" ")[0] : ""} 💛`
                      : (weekly?.streak_days >= 2)
                        ? `Welcome back${user?.name ? ", " + user.name.split(" ")[0] : ""} 👋`
                        : (user?.name ? `Hi ${user.name.split(" ")[0]}` : "Hello")}
                </div>
                <h2 className="font-serif-display text-2xl sm:text-3xl md:text-4xl text-primary tracking-tight leading-tight" data-testid="chat-greeting">
                  {weekly?.memory_greeting
                    ? weekly.memory_greeting
                    : (weekly?.has_data && weekly?.days_since_last_log >= 3)
                      ? "Ready to pick up where we left off?"
                      : (weekly?.streak_days >= 2)
                        ? `That's a ${weekly.streak_days}-day streak — what are we having today?`
                        : "What are we having today?"}
                </h2>
                <p className="text-foreground/70 mt-3 text-sm" data-testid="chat-empty-subtext">
                  {weekly?.memory_greeting
                    ? "No pressure — tell me what you'd like help with today. 🌿"
                    : (weekly?.has_data && weekly?.days_since_last_log >= 3)
                      ? "It's been a few days — no worries at all. 💛"
                      : (weekly?.streak_days >= 2)
                        ? "Genuinely love that consistency — let's keep it going."
                        : "I'm your food & health companion — tell me what you ate, how your day's going, or what you'd like help with. No pressure, no judgment, ever. 🌿"}
                </p>
                <WeekInPatterns data={weekly} />
                <SavedPlanCard compact />
                {freshStart && sessions.length > 0 && (
                  <button
                    onClick={loadHistory}
                    data-testid="load-history-btn"
                    className="mt-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-secondary transition"
                  >
                    <History size={12} /> Resume previous chat
                  </button>
                )}
              </div>
            )}

            <div className="space-y-4 max-w-3xl mx-auto">
              {messages.map((m) => <Bubble key={m.id} role={m.role} content={m.content} createdAt={m.created_at} plan={m.plan} logged={m.logged} recipeSuggestion={m.recipe_suggestion} recipeSuggestions={m.recipe_suggestions} failed={m.failed} onRetry={() => retry(m)} onOpenRecipe={openRecipe} onEditLog={editLog} onSavePlan={savePlan} />)}
              {streaming && (activePlan || !partial) && (
                <AgentPlan steps={activePlan} planReady={planReady} />
              )}
              {partial && <Bubble role="assistant" content={partial} streaming />}
              {streaming && stalled && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground msg-in pl-1" data-testid="chat-stalled-hint">
                  <RotateCcw size={13} className="animate-spin text-secondary" />
                  Connection looks slow — still trying to reach KAAYA…
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-border bg-background/60 backdrop-blur-xl p-4">
            {messages.length === 0 && !partial && (
              <div className="max-w-3xl mx-auto mb-2.5 flex gap-2 overflow-x-auto no-scrollbar" data-testid="chat-starters">
                {buildStarters(user?.profile).map((s, i) => (
                  <button
                    key={i}
                    onClick={() => onStarter(s)}
                    data-testid={`starter-${i}`}
                    className="inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-border bg-surface text-foreground/80 hover:border-secondary/40 hover:bg-secondary/5 active:scale-95 transition"
                  >
                    <Sparkles size={12} className="text-secondary" /> {s.label}
                  </button>
                ))}
              </div>
            )}
            <div className="max-w-3xl mx-auto flex items-end gap-2 rounded-3xl border border-border bg-surface px-2 py-2 shadow-sm focus-within:ring-2 focus-within:ring-primary/40 transition">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="h-10 w-10 shrink-0 flex items-center justify-center rounded-full text-secondary hover:bg-muted active:scale-95 cursor-pointer transition"
                    data-testid="chat-photo-label"
                    aria-label="Add a meal photo"
                  >
                    <Camera size={20} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="top" className="rounded-2xl">
                  <DropdownMenuItem onClick={() => cameraInputRef.current?.click()} data-testid="chat-photo-camera">
                    <Camera size={15} className="mr-2 text-secondary" /> Take a photo
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => galleryInputRef.current?.click()} data-testid="chat-photo-gallery">
                    <ImageIcon size={15} className="mr-2 text-secondary" /> Upload from gallery
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => { pickPhoto(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="chat-photo-input-camera"
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={(e) => { pickPhoto(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="chat-photo-input"
              />
              <VoiceButton
                testId="chat-voice-btn"
                onTranscript={(t) => { setInput((prev) => (prev ? prev.trim() + " " : "") + t); inputRef.current?.focus(); }}
              />
              <Textarea
                data-testid="chat-input-field"
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask KAAYA anything…"
                rows={1}
                className="flex-1 resize-none min-h-[40px] max-h-40 py-2 px-1 text-sm rounded-none bg-transparent border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-sm placeholder:truncate"
              />
              {streaming ? (
                <Button
                  onClick={stopStreaming}
                  data-testid="chat-stop-btn"
                  title="Stop generating"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                >
                  <Square size={15} className="fill-current" />
                </Button>
              ) : (
                <Button
                  onClick={() => send()}
                  disabled={!input.trim()}
                  data-testid="chat-send-btn"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground disabled:opacity-40"
                >
                  <Send size={18} />
                </Button>
              )}
            </div>
          </div>
        </section>
      </div>
      <DeductionDialog trigger={deductTrigger} onClose={() => setDeductTrigger(null)} />
      <MealReviewDialog trigger={reviewTrigger} onClose={() => setReviewTrigger(null)} onApplied={() => refresh()} />

      <RecipeCardDialog
        open={recipeOpen}
        recipe={recipe}
        loading={recipeLoading}
        liked={recipeLiked}
        onToggleLike={toggleLikeRecipe}
        onClose={() => setRecipeOpen(false)}
        onCook={cookRecipe}
        cooking={cooking}
      />

      <Dialog open={!!photoDraft} onOpenChange={(o) => { if (!o) closePhotoDraft(); }}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="chat-photo-note-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">Add a note</DialogTitle></DialogHeader>
          {photoDraft?.url && (
            <img src={photoDraft.url} alt="meal preview" className="w-full max-h-64 object-cover rounded-2xl border border-border" data-testid="chat-photo-preview" />
          )}
          <Textarea
            autoFocus
            value={photoNote}
            onChange={(e) => setPhotoNote(e.target.value)}
            placeholder="Add context — e.g. “my lunch”, “half portion”, “homemade”…"
            className="resize-none min-h-[64px] rounded-2xl bg-surface border-border mt-1"
            data-testid="chat-photo-note-input"
          />
          <DialogFooter className="gap-2">
            <Button variant="ghost" className="rounded-full" onClick={closePhotoDraft} data-testid="chat-photo-cancel">Cancel</Button>
            <Button
              className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground"
              onClick={() => { const f = photoDraft.file; const n = photoNote; closePhotoDraft(); uploadMealPhoto(f, n); }}
              data-testid="chat-photo-send"
            >
              <Send size={15} className="mr-1" /> Send photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="support-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">How can we help?</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <button
              onClick={() => setSupportType("support")}
              data-testid="support-type-support"
              className={`rounded-2xl border p-3 text-left transition active:scale-[0.98] ${supportType === "support" ? "border-secondary bg-secondary/10" : "border-border hover:bg-muted"}`}
            >
              <div className="text-sm font-medium text-primary">Get support</div>
              <div className="text-xs text-foreground/70 mt-0.5">Something's not working or you need help.</div>
            </button>
            <button
              onClick={() => setSupportType("feedback")}
              data-testid="support-type-feedback"
              className={`rounded-2xl border p-3 text-left transition active:scale-[0.98] ${supportType === "feedback" ? "border-secondary bg-secondary/10" : "border-border hover:bg-muted"}`}
            >
              <div className="text-sm font-medium text-primary">Share feedback</div>
              <div className="text-xs text-foreground/70 mt-0.5">An idea, suggestion, or what you love.</div>
            </button>
          </div>
          <Textarea
            autoFocus
            value={supportText}
            onChange={(e) => setSupportText(e.target.value)}
            placeholder={supportType === "feedback" ? "Tell us what you think — features you'd love, what's working well…" : "Describe the issue or question — the more detail, the faster we can help."}
            className="resize-none min-h-[120px] rounded-2xl bg-surface border-border mt-2"
            data-testid="support-message-input"
          />
          <DialogFooter className="gap-2">
            <Button variant="ghost" className="rounded-full" onClick={() => setSupportOpen(false)} data-testid="support-cancel">Cancel</Button>
            <Button
              className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground"
              onClick={submitSupport}
              disabled={supportSubmitting || !supportText.trim()}
              data-testid="support-submit"
            >
              {supportSubmitting ? "Sending…" : <><Send size={15} className="mr-1" /> Send</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (sameDay) return time;
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  const day = d.toDateString() === yest.toDateString() ? "Yesterday" : d.toLocaleDateString([], { month: "short", day: "numeric" });
  return `${day} · ${time}`;
}

function Bubble({ role, content, createdAt, streaming, plan, logged, recipeSuggestion, recipeSuggestions, failed, onRetry, onOpenRecipe, onEditLog, onSavePlan }) {
  const ts = fmtTime(createdAt);
  if (role === "user") {
    return (
      <div className="flex items-start justify-end gap-2.5 msg-in">
        <div className="flex flex-col items-end gap-1 min-w-0">
          <div className="max-w-full rounded-2xl rounded-br-md bg-secondary text-secondary-foreground px-4 py-2.5 text-[15px] leading-relaxed shadow-sm whitespace-pre-wrap break-words" data-testid="msg-user">
            {content}
          </div>
          {ts && (
            <span className="text-[10px] text-muted-foreground tabular-nums pr-1" data-testid="msg-user-time">{ts}</span>
          )}
          {failed && (
            <button
              onClick={onRetry}
              data-testid="msg-retry-btn"
              className="flex items-center gap-1.5 text-xs text-destructive hover:text-destructive/80 active:scale-95 transition"
            >
              <RotateCcw size={13} /> Reply didn't come through · Tap to retry
            </button>
          )}
        </div>
        <div className="shrink-0 w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center mt-0.5" data-testid="msg-user-avatar">
          <User size={16} className="text-primary" strokeWidth={1.75} />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2.5 msg-in">
      <div className="shrink-0 w-8 h-8 rounded-full bg-secondary/15 flex items-center justify-center mt-0.5" data-testid="msg-assistant-avatar">
        <ChefHat size={16} className="text-secondary" strokeWidth={1.75} />
      </div>
      <div className="flex flex-col items-start gap-1 flex-1 min-w-0">
      <div className="w-full rounded-2xl rounded-bl-md bg-surface border border-border px-4 py-3 shadow-sm" data-testid="msg-assistant">
        {plan && plan.length > 1 && <AgentPlanChips steps={plan} />}
        <div className={`text-foreground/90 leading-relaxed ${streaming ? "cursor-blink" : ""}`}>
          <Markdown>{content}</Markdown>
        </div>
        {recipeSuggestion?.dish && (
          <button
            onClick={() => onOpenRecipe?.(recipeSuggestion)}
            data-testid="open-recipe-card-btn"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium px-3.5 py-2 rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 active:scale-95 transition shadow-sm"
            title="Open the full recipe card to like, save or download"
          >
            <ChefHat size={15} /> View recipe card
            <span className="opacity-80 font-normal">· like &amp; download</span>
          </button>
        )}
        {recipeSuggestions && recipeSuggestions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="recipe-card-buttons">
            {recipeSuggestions.map((sug, i) => (
              <button
                key={sug.dish || `sug-${i}`}
                onClick={() => onOpenRecipe?.(sug)}
                data-testid={`open-recipe-card-btn-${i}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3.5 py-2 rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 active:scale-95 transition shadow-sm"
                title={`Open the full recipe for ${sug.dish}`}
              >
                <ChefHat size={15} />
                <span className="capitalize opacity-80 font-normal">{sug.meal}:</span>
                {sug.dish}
              </button>
            ))}
          </div>
        )}
        {recipeSuggestions && new Set(recipeSuggestions.map((s) => s.meal)).size >= 2 && (
          <button
            onClick={() => onSavePlan?.(recipeSuggestions)}
            data-testid="save-plan-btn"
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium px-3.5 py-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 transition shadow-sm"
            title="Save this plan to your dashboard and tick meals off"
          >
            <Plus size={15} /> Save plan to my dashboard
          </button>
        )}
        {logged && (
          <button
            onClick={() => onEditLog?.(logged)}
            data-testid="added-to-tracker-chip"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15 active:scale-95 transition"
            title="Edit or review this logged meal"
          >
            <Check size={13} strokeWidth={2.5} />
            Added to tracker · {Math.round(logged.calories)} kcal
            <Pencil size={11} className="ml-1 opacity-70" />
          </button>
        )}
      </div>
      {ts && !streaming && (
        <span className="text-[10px] text-muted-foreground tabular-nums pl-1" data-testid="msg-assistant-time">{ts}</span>
      )}
      </div>
    </div>
  );
}

const AGENT_ICON = {
  calorie: Flame,
  recipe: ChefHat,
  diet: HeartPulse,
  inventory: Boxes,
  workout: Dumbbell,
  micro: PillIcon,
  general: Sparkles,
};

function AgentPlan({ steps, planReady }) {
  const planning = !steps || steps.length === 0;
  return (
    <div className="msg-in flex items-start gap-2.5" data-testid="agent-plan-live">
      <div className="shrink-0 mt-0.5 h-8 w-8 rounded-full bg-secondary/15 border border-secondary/25 flex items-center justify-center text-secondary cooking-avatar">
        <ChefHat size={16} />
      </div>
      <div className="flex-1 min-w-0 rounded-2xl rounded-bl-md bg-surface border border-border/70 p-4 shadow-sm">
        <div className="flex items-center gap-2.5 mb-1">
          <span className="cooking-loader" aria-hidden="true">
            <span className="steam s1" /><span className="steam s2" /><span className="steam s3" />
            <span className="pot" />
          </span>
          <span className="text-sm font-semibold text-primary cooking-text" data-testid="agent-plan-status">
            {planReady ? "Plating up your reply" : "Cooking up your reply"}
            <span className="cooking-dots"><i>.</i><i>.</i><i>.</i></span>
          </span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          {planReady ? "Almost there — bringing it all together." : "KAAYA is on it. This can take a few seconds."}
        </p>

        <div className="cooking-bar mb-1" aria-hidden="true"><span /></div>

        {!planning && (
          <div className="space-y-2 mt-3" data-testid="agent-plan-steps">
            {steps.map((s, i) => {
              const Icon = AGENT_ICON[s.icon] || AGENT_ICON[s.agent] || Sparkles;
              return (
                <div key={i} className="flex items-start gap-2.5 text-sm" data-testid={`agent-step-${i}`}>
                  <span className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full shrink-0 ${planReady ? "bg-secondary/15 text-secondary" : "bg-secondary/15 text-secondary animate-pulse"}`}>
                    {planReady ? <Check size={12} /> : <Icon size={12} />}
                  </span>
                  <div className="min-w-0">
                    <span className="font-medium text-primary">{s.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function AgentPlanChips({ steps }) {
  return (
    <div className="flex flex-wrap gap-1.5 mb-3" data-testid="agent-plan-chips">
      {steps.map((s, i) => {
        const Icon = AGENT_ICON[s.icon] || AGENT_ICON[s.agent] || Sparkles;
        return (
          <span
            key={i}
            data-testid={`agent-chip-${i}`}
            className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-secondary/10 text-secondary border border-secondary/20"
          >
            <Icon size={11} /> {s.label}
          </span>
        );
      })}
    </div>
  );
}


// Chat is now a single continuous thread — the session list, mini macro card and rename row were removed.

